import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Repeat, Trash2, ShieldCheck, Music2, CheckCircle2, AlertCircle, FileArchive, Sparkles, SlidersHorizontal, SlidersVertical, GitCompare, FileDown, Zap, Globe, Smartphone, Video, Clock, Gem } from "lucide-react";
import { tiktokStatus, connectTikTok } from "@/lib/tiktok";
import { TikTokPostForm } from "@/components/TikTokPostForm";
import SurgeLayerPanel from "@/components/SurgeLayerPanel";
import MixBusPanel from "@/components/MixBusPanel";
import ABCompare from "@/components/ABCompare";
import SpectrumCurve from "@/components/SpectrumCurve";
import { downloadMidi } from "@/lib/midi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import WaveformPlayer from "@/components/WaveformPlayer";
import AudioEffectsPanel from "@/components/AudioEffectsPanel";
import { audioUrl, API } from "@/lib/api";
import { uploadBlobChunked } from "@/lib/upload";
import { AiSourceTag } from "@/components/ModelPreference";
import QaBadge from "@/components/QaBadge";

const PROVIDER_LABEL = { openai: "ChatGPT", claude: "Claude" };
const STAGE_LABELS = {
  render: "Composing your track with AI… this can take up to a minute.",
  download: "Fetching the rendered audio…",
  encode: "Encoding MP3…",
  store: "Saving files…",
  loop: "Building the seamless loop…",
  analyze: "Detecting BPM & key…",
  stems: "Rendering stems (drums, bass, melody)…",
};

function TikTokPublish({ gen, sv }) {
  const [tt, setTt] = useState(null);
  const [open, setOpen] = useState(false);
  const [creator, setCreator] = useState(null);
  const [post, setPost] = useState(sv?.tiktok || null);
  const [sched, setSched] = useState(sv?.tiktok_schedule || null);
  useEffect(() => { tiktokStatus().then(setTt); }, []);
  useEffect(() => {
    if (!post || ["PUBLISH_COMPLETE", "FAILED"].includes(post.status)) return undefined;
    const t = setInterval(() => api.get(`/generations/${gen.id}/tiktok-status`).then(({ data }) => { setPost(data); if (data.status === "PUBLISH_COMPLETE") toast.success("Published on TikTok!"); if (data.status === "FAILED") toast.error(`TikTok: ${data.fail_reason || "publish failed"}`); }).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [post, gen.id]);
  useEffect(() => {
    if (sched?.status !== "scheduled") return undefined;
    const t = setInterval(() => api.get(`/generations/${gen.id}`).then(({ data }) => { const s = data.social_video?.tiktok_schedule; setSched(s || null); if (s?.status === "published" && data.social_video?.tiktok) setPost(data.social_video.tiktok); }).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [sched, gen.id]);
  const openForm = async () => {
    setOpen(true);
    try { const { data } = await api.get("/tiktok/creator"); setCreator(data); }
    catch (e) { toast.error(e.response?.data?.detail || "TikTok creator info unavailable"); setOpen(false); }
  };
  const unschedule = async () => {
    try { await api.delete(`/generations/${gen.id}/tiktok-schedule`); setSched(null); toast.success("Scheduled post cancelled"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };
  if (!tt) return null;
  if (!tt.configured) return <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1" title={tt.note} data-testid={`tiktok-unavailable-${gen.seq}`}><Video size={12} /> TikTok publishing coming soon</span>;
  if (!tt.account) return <Button variant="outline" size="sm" onClick={() => connectTikTok().catch((e) => toast.error(e.response?.data?.detail || "Could not start TikTok login"))} className="gap-1.5 border-border" data-testid={`tiktok-connect-${gen.seq}`}><Video size={14} /> Connect TikTok</Button>;
  if (post && post.status !== "FAILED") return <span className={`text-xs inline-flex items-center gap-1 ${post.status === "PUBLISH_COMPLETE" ? "text-emerald-400" : "text-amber-400"}`} data-testid={`tiktok-post-${gen.seq}`}>{post.status === "PUBLISH_COMPLETE" ? <CheckCircle2 size={13} /> : <Loader2 size={13} className="animate-spin" />} TikTok: {post.status === "PUBLISH_COMPLETE" ? "published" : "processing"} · {post.privacy_level}</span>;
  if (sched?.status === "scheduled") {
    return (
      <span className="text-xs inline-flex flex-wrap items-center gap-1.5 text-sky-300" data-testid={`tiktok-scheduled-${gen.seq}`}>
        <Clock size={13} /> TikTok scheduled · {new Date(sched.scheduled_at).toLocaleString()} · {sched.privacy_level}{sched.remind_minutes ? ` · reminder ${sched.remind_minutes >= 1440 ? "1 day" : `${sched.remind_minutes} min`} before` : ""}
        <button type="button" onClick={unschedule} className="underline text-muted-foreground" data-testid={`tiktok-unschedule-${gen.seq}`}>Cancel</button>
      </span>
    );
  }
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${open ? "w-full basis-full" : ""}`}>
      {sched?.status === "failed" && <span className="w-full text-[11px] text-red-400" data-testid={`tiktok-schedule-failed-${gen.seq}`}>Scheduled post failed: {sched.error}</span>}
      {!open && <Button variant="outline" size="sm" onClick={openForm} className="gap-1.5 border-border" data-testid={`tiktok-publish-${gen.seq}`}><Video size={14} /> Publish to TikTok as @{tt.account.display_name || "you"}</Button>}
      {open && creator && <TikTokPostForm gen={gen} tt={tt} creator={creator} onPosted={(d) => { setPost(d); setOpen(false); }} onScheduled={(d) => { setSched(d); setOpen(false); }} onClose={() => setOpen(false)} />}
    </div>
  );
}

function SocialVideoButton({ gen }) {
  const [sv, setSv] = useState(gen.social_video || null);
  const [seconds, setSeconds] = useState(gen.social_video?.seconds || 30);
  const [busy, setBusy] = useState(false);
  const processing = sv?.status === "processing";
  useEffect(() => {
    if (!processing) return undefined;
    const t = setInterval(() => api.get(`/generations/${gen.id}`).then(({ data }) => { if (data.social_video?.status !== "processing") { setSv(data.social_video); if (data.social_video?.status === "completed") toast.success("Vertical clip ready — download and upload it to TikTok / Reels."); else toast.error(data.social_video?.error || "Clip failed."); } }).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [processing, gen.id]);
  const render = async () => {
    setBusy(true);
    try { await api.post(`/generations/${gen.id}/social-video`, { seconds }); setSv({ status: "processing", seconds }); toast.success(`Rendering a ${seconds}s vertical clip (~30 s)…`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start the render."); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`social-video-${gen.seq}`}>
      {sv?.status === "completed" && sv.path && (
        <a href={audioUrl(sv.path)} download={`musicflow-${gen.seq}-tiktok-${sv.seconds}s.mp4`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-pink-500/50 text-pink-400 hover:bg-pink-500/10 text-sm" data-testid={`social-video-download-${gen.seq}`}><Smartphone size={14} /> MP4 9:16 · {sv.seconds}s</a>
      )}
      {sv?.status === "completed" && sv.path && <TikTokPublish gen={gen} sv={sv} />}
      <select value={seconds} onChange={(e) => setSeconds(Number(e.target.value))} disabled={processing} className="h-9 rounded-md bg-background border border-border px-2 text-xs" aria-label="Clip length" data-testid={`social-video-seconds-${gen.seq}`}>{[15, 30, 60].map((s) => <option key={s} value={s}>{`${s}s`}</option>)}</select>
      <Button variant="outline" size="sm" onClick={render} disabled={busy || processing} className="gap-1.5 border-pink-500/40 text-pink-400 hover:bg-pink-500/10" title="Export a vertical 9:16 clip with animated waveform for TikTok, Reels and Shorts (free)" data-testid={`social-video-render-${gen.seq}`}>
        {processing ? <Loader2 size={13} className="animate-spin" /> : <Smartphone size={14} />} {processing ? "Rendering…" : sv?.status === "completed" ? "Re-export" : "TikTok / Reels clip"}
      </Button>
    </div>
  );
}

const SHOWCASE_LABEL = { pending: "In review", approved: "In showcase", rejected: "Not accepted" };

function ShowcaseButton({ gen }) {
  const [status, setStatus] = useState(gen.showcase?.status || null);
  const [busy, setBusy] = useState(false);
  const live = status === "pending" || status === "approved";
  const act = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/generations/${gen.id}/showcase`, { action: live ? "withdraw" : "submit" });
      setStatus(data.status);
      toast.success(data.status === "approved" ? "Published in the public showcase." : data.status === "pending" ? "Submitted — an admin will review it shortly." : "Removed from the showcase.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update showcase."); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="outline" size="sm" onClick={act} disabled={busy} title={live ? "Withdraw from the public gallery" : "Publish this track in the public gallery (reviewed by an admin)"}
      className={`gap-1.5 ${status === "approved" ? "border-emerald-500/50 text-emerald-400" : status === "pending" ? "border-amber-500/50 text-amber-400" : "border-border"}`} data-testid={`showcase-toggle-${gen.seq}`}>
      {busy ? <Loader2 size={13} className="animate-spin" /> : <Globe size={14} />} {live ? SHOWCASE_LABEL[status] : status === "rejected" ? "Resubmit" : "Publish to showcase"}
    </Button>
  );
}

export default function GenerationCard({ gen, onVariation, onDelete, varLoading, canModify = true, onSaved }) {
  const [licenseOpen, setLicenseOpen] = useState(false);
  const [fxOpen, setFxOpen] = useState(false);
  const [surgeOpen, setSurgeOpen] = useState(false);
  const [mixOpen, setMixOpen] = useState(false);
  const [aiMetas, setAiMetas] = useState([]);
  const [aiMetaLoading, setAiMetaLoading] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [aiProvider, setAiProvider] = useState("claude");
  const [applied, setApplied] = useState(null);
  const [meta, setMeta] = useState(gen.metadata || {});
  const navigate = useNavigate();
  const metaSource = `${gen.prompt} (${gen.genre}, ${gen.mood})`;

  const suggestMeta = async () => {
    setAiMetaLoading(true);
    try {
      const { data } = await api.post("/ai/metadata", { source: metaSource, provider: aiProvider });
      setAiMetas([{ provider: aiProvider, data }]);
      setApplied(null);
      toast.success("AI suggested a name & tags ✨");
    } catch {
      toast.error("Could not generate metadata.");
    } finally { setAiMetaLoading(false); }
  };

  const compareMeta = async () => {
    setCompareLoading(true);
    try {
      const [o, cl] = await Promise.all([
        api.post("/ai/metadata", { source: metaSource, provider: "openai" }),
        api.post("/ai/metadata", { source: metaSource, provider: "claude" }),
      ]);
      setAiMetas([{ provider: "openai", data: o.data }, { provider: "claude", data: cl.data }]);
      setApplied(null);
    } catch {
      toast.error("Comparaison impossible.");
    } finally { setCompareLoading(false); }
  };

  const applyMeta = async (entry) => {
    try {
      const { data } = await api.post(`/generations/${gen.id}/metadata`, { title: entry.data.title, tags: entry.data.tags, description: entry.data.description, provider: entry.provider });
      window.dispatchEvent(new CustomEvent("ai-preference-updated", { detail: (await api.get("/ai/preference")).data }));
      setMeta(data.metadata || meta);
      setApplied(entry.provider);
      toast.success(`Saved to track (${PROVIDER_LABEL[entry.provider]}) — visible & filterable in your Library.`);
    } catch {
      toast.error("Could not save metadata.");
    }
  };

  const saveFx = async (blob, settings, name) => {
    const tid = toast.loading("Envoi de la piste traitée…");
    try {
      const uploadId = await uploadBlobChunked(blob, (i, n) => toast.loading(`Envoi de la piste traitée… ${i}/${n}`, { id: tid }));
      await api.post(`/generations/${gen.id}/fx-save`, { upload_id: uploadId, name: name || null, settings });
      toast.success("Nouvelle piste enregistrée dans la Librairie ✨", { id: tid });
      setFxOpen(false);
      onSaved?.();
    } catch {
      toast.error("Enregistrement impossible. Réessayez.", { id: tid });
    }
  };

  const goFilter = (q) => navigate(`/dashboard/library?q=${encodeURIComponent(q)}`);
  const processing = gen.status === "processing";
  const failed = gen.status === "failed";
  const af = gen.audio_files || {};
  const stems = gen.stems_files || {};

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border bg-card p-5 hover:border-primary/30 transition-colors min-w-0 overflow-hidden"
      data-testid={`generation-card-${gen.seq}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-display font-bold text-lg" data-testid={`gen-title-${gen.seq}`}>{meta.title || `Generation #${gen.seq}`}</span>
            {processing && <Badge className="bg-accent/20 text-accent border-accent/30 gap-1"><Loader2 size={11} className="animate-spin" /> Rendering</Badge>}
            {gen.status === "completed" && <Badge className="bg-primary/15 text-primary border-primary/30 gap-1"><CheckCircle2 size={11} /> Ready</Badge>}
            {gen.engine === "premium" && gen.engine_used && gen.engine_used !== "internal" && <Badge className="bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30" title="Rendu par le moteur premium (Eleven Music)" data-testid={`premium-badge-${gen.id}`}>Premium · Eleven Music</Badge>}
            {gen.engine === "premium" && !gen.engine_used && processing && <Badge className="bg-fuchsia-500/10 text-fuchsia-200 border-fuchsia-500/30 gap-1" title="Rendu en cours par le moteur premium (repli interne automatique si indisponible)" data-testid={`premium-pending-badge-${gen.id}`}><Gem size={10} /> Premium (rendu…)</Badge>}
            {gen.engine === "premium" && gen.engine_used === "internal" && <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30" title={gen.fallback_reason || "Moteur premium indisponible"} data-testid={`premium-fallback-badge-${gen.id}`}>Repli interne</Badge>}
            {failed && <Badge className="bg-destructive/20 text-destructive border-destructive/30 gap-1"><AlertCircle size={11} /> Failed</Badge>}
            {meta.ai_provider && <AiSourceTag provider={meta.ai_provider} prefix="généré par" testId={`meta-ai-label-${gen.seq}`} />}
          </div>
          <p className="text-sm text-muted-foreground line-clamp-1">{gen.prompt}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <Chip>{gen.genre}</Chip>
            <Chip>{gen.mood}</Chip>
            <Chip>{gen.duration}s</Chip>
            {gen.loop && <Chip>Loop</Chip>}
            {gen.stems && <Chip>Stems</Chip>}
            {gen.parent_id && gen.kind !== "fx" && <Chip>Variation</Chip>}
            {gen.kind === "fx" && <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-secondary/15 text-secondary border border-secondary/30" data-testid={`fx-chip-${gen.seq}`}>FX</span>}
            {gen.kind === "synth" && <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid={`synth-chip-${gen.seq}`} title={gen.synth?.patch_name}>{gen.metadata?.engine === "surge" || gen.synth?.engine === "surge" ? "Surge XT" : "Synth"} · {gen.synth?.patch_name}</span>}
            {gen.kind === "layered" && <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid={`layered-chip-${gen.seq}`} title={`Couche ${gen.synth_layer?.kind} · mix ${Math.round((gen.synth_layer?.mix || 0) * 100)}%`}>+ Surge {gen.synth_layer?.patch_name}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {gen.status === "completed" && gen.audio_files?.mp3 && canModify && <ShowcaseButton gen={gen} />}
          <Dialog open={licenseOpen} onOpenChange={setLicenseOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="border-border gap-1.5" data-testid={`view-license-${gen.seq}`}>
                <ShieldCheck size={14} className="text-primary" /> License
              </Button>
            </DialogTrigger>
            <DialogContent className="glass border-border">
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <ShieldCheck className="text-primary" size={20} /> Commercial License
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <Row k="License ID" v={<span className="font-mono">{gen.license?.id}</span>} />
                <Row k="Project" v={gen.license?.project} />
                <Row k="Type" v={gen.license?.type} />
                <Row k="Status" v={<span className="text-primary font-semibold">{gen.license?.status}</span>} />
                <div className="pt-2 border-t border-border space-y-2">
                  {(gen.license?.rights || []).map((r) => (
                    <div key={r} className="flex items-center gap-2 text-foreground">
                      <CheckCircle2 size={15} className="text-primary" /> {r}
                    </div>
                  ))}
                </div>
                <a
                  href={`${API}/licenses/${gen.id}/pdf`}
                  className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 text-sm font-semibold transition-colors"
                  data-testid={`license-pdf-${gen.seq}`}
                >
                  <FileDown size={15} /> Télécharger le certificat PDF
                </a>
              </div>
            </DialogContent>
          </Dialog>
          <Button variant="ghost" size="icon" onClick={() => onDelete(gen.id)} data-testid={`delete-generation-${gen.seq}`} className={`text-muted-foreground hover:text-destructive ${canModify ? "" : "hidden"}`}>
            <Trash2 size={16} />
          </Button>
        </div>
      </div>

      {processing && (
        <div className="flex items-center gap-3 text-muted-foreground text-sm py-6 justify-center" data-testid={`gen-stage-${gen.seq}`}>
          <Loader2 className="animate-spin text-primary" size={18} />
          {STAGE_LABELS[gen.stage] || STAGE_LABELS.render}{gen.retries ? ` (retry ${gen.retries})` : ""}
        </div>
      )}

      {failed && (
        <div className="text-sm text-destructive/90 py-3 px-4 rounded-lg bg-destructive/10 border border-destructive/20">
          {gen.error || "Generation failed."} Your credits were automatically refunded — try again.
        </div>
      )}

      {gen.status === "completed" && (
        <div className="space-y-2.5">
          {meta && (meta.bpm || meta.key || (meta.tags || []).length > 0) && (
            <div className="flex flex-wrap items-center gap-2 mb-1" data-testid={`metadata-${gen.seq}`}>
              {meta.bpm && <MetaBadge label="BPM" value={meta.bpm} onClick={() => goFilter(String(meta.bpm))} testId={`bpm-badge-${gen.seq}`} />}
              {meta.key && <MetaBadge label="KEY" value={meta.key} onClick={() => goFilter(meta.key)} testId={`key-badge-${gen.seq}`} />}
              {canModify && gen.kind !== "fx" && <QaBadge gen={gen} onUpdated={() => onSaved?.()} />}
              {(meta.tags || []).map((t) => (
                <button key={t} onClick={() => goFilter(t)} data-testid={`tag-${t}-${gen.seq}`} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-secondary/15 text-secondary border border-secondary/30 hover:bg-secondary/30 transition-colors">#{t}</button>
              ))}
            </div>
          )}
          {af.mp3 && <WaveformPlayer src={audioUrl(af.mp3)} label="Full Mix — MP3" downloadName={`generation-${gen.seq}.mp3`} testId={`player-mp3-${gen.seq}`} />}
          {af.wav && <WaveformPlayer src={audioUrl(af.wav)} label="Full Mix — WAV (lossless)" downloadName={`generation-${gen.seq}.wav`} testId={`player-wav-${gen.seq}`} />}
          {af.loop && <WaveformPlayer src={audioUrl(af.loop)} label="Loopable Version" downloadName={`generation-${gen.seq}-loop.wav`} testId={`player-loop-${gen.seq}`} />}
          {gen.kind === "mix" && gen.parent_id && <ABCompare gen={gen} kind="music" testId={`ab-${gen.seq}`} />}
          {gen.kind !== "mix" && meta?.qa?.spectrum && <SpectrumCurve spectrum={meta.qa.spectrum} height={56} testId={`spectrum-${gen.seq}`} />}

          {Object.keys(stems).length > 0 && (
            <div className="pt-2">
              <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground mb-2">
                <Music2 size={13} /> STEMS
              </div>
              <div className="space-y-2.5">
                {Object.entries(stems).map(([name, path]) => (
                  <WaveformPlayer key={name} src={audioUrl(path)} label={`Stem — ${name}`} downloadName={`generation-${gen.seq}-${name}.wav`} testId={`player-stem-${name}-${gen.seq}`} />
                ))}
              </div>
            </div>
          )}

          <div className="pt-2 flex flex-wrap gap-2">
            {canModify && af.mp3 && gen.kind !== "fx" && <SocialVideoButton gen={gen} />}
            {canModify && (
              <Button
                onClick={() => onVariation(gen.id)}
                disabled={varLoading}
                variant="outline"
                className="border-secondary/40 text-secondary hover:bg-secondary/10 gap-2"
                data-testid={`generate-variation-${gen.seq}`}
              >
                {varLoading ? <Loader2 className="animate-spin" size={15} /> : <Repeat size={15} />}
                Generate Variation
              </Button>
            )}
            <Button onClick={suggestMeta} disabled={aiMetaLoading} variant="outline" className="border-primary/40 text-primary hover:bg-primary/10 gap-2" data-testid={`ai-metadata-${gen.seq}`}>
              {aiMetaLoading ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />}
              AI name & tags
            </Button>
            <button type="button" onClick={() => setAiProvider((p) => (p === "openai" ? "claude" : "openai"))} className="text-[10px] font-mono px-2.5 py-1 rounded-full bg-primary/15 text-primary border border-primary/30 self-center" data-testid={`metadata-provider-toggle-${gen.seq}`} title="Switch AI model">
              {PROVIDER_LABEL[aiProvider]}
            </button>
            <Button onClick={compareMeta} disabled={compareLoading} variant="ghost" className="text-secondary hover:text-secondary hover:bg-secondary/10 gap-1.5" data-testid={`ai-metadata-compare-${gen.seq}`}>
              {compareLoading ? <Loader2 className="animate-spin" size={15} /> : <GitCompare size={15} />} Comparer
            </Button>
            <a
              href={`${API}/generations/${gen.id}/download-zip`}
              className="inline-flex items-center gap-2 rounded-md border border-primary/40 text-primary hover:bg-primary/10 h-10 px-4 text-sm font-medium transition-colors"
              data-testid={`download-zip-${gen.seq}`}
            >
              <FileArchive size={15} /> Download ZIP (mix + stems)
            </a>
            {(af.mp3 || af.wav) && (
              <Dialog open={fxOpen} onOpenChange={setFxOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-secondary/40 text-secondary hover:bg-secondary/10 gap-2" data-testid={`fx-open-${gen.seq}`}>
                    <SlidersHorizontal size={15} /> Effets
                  </Button>
                </DialogTrigger>
                <DialogContent className="glass border-border max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                      <SlidersHorizontal className="text-secondary" size={20} /> Rack d'effets — {meta.title || `Generation #${gen.seq}`}
                    </DialogTitle>
                    <DialogDescription>Reverb, écho, EQ, vitesse et fondus — traitement local, export WAV ou nouvelle piste.</DialogDescription>
                  </DialogHeader>
                  <AudioEffectsPanel src={audioUrl(af.wav || af.mp3)} downloadName={`generation-${gen.seq}-fx.wav`} onSave={saveFx} defaultName={`${meta.title || `Generation #${gen.seq}`} (FX)`} />
                </DialogContent>
              </Dialog>
            )}
            {gen.kind === "synth" && gen.synth?.notes?.length > 0 && (
              <Button variant="outline" onClick={() => downloadMidi(gen.synth.notes, gen.synth.bpm || 120, `${meta.title || gen.prompt || "pattern"}`)} className="gap-2 border-amber-500/40 text-amber-400 hover:bg-amber-500/10" title="Motif en .mid pour Surge XT / DAW" data-testid={`midi-export-${gen.seq}`}>
                <FileDown size={15} /> MIDI
              </Button>
            )}
            {af.wav && gen.kind !== "synth" && canModify && (
              <Button variant="outline" onClick={() => setSurgeOpen((o) => !o)} className={`gap-2 ${surgeOpen ? "bg-amber-500/15 border-amber-500/50 text-amber-400" : "border-amber-500/40 text-amber-400 hover:bg-amber-500/10"}`} data-testid={`surge-layer-open-${gen.seq}`}>
                <Zap size={15} /> Couche Surge
              </Button>
            )}
            {(af.wav || af.mp3) && canModify && (
              <Button variant="outline" onClick={() => setMixOpen((o) => !o)} className={`gap-2 ${mixOpen ? "bg-cyan-500/15 border-cyan-500/50 text-cyan-300" : "border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"}`} title="Réverb, channel strip, modulation, sidechain voix, limiteur — gratuit" data-testid={`mixbus-open-${gen.seq}`}>
                <SlidersVertical size={15} /> Mix Bus
              </Button>
            )}
          </div>
          {surgeOpen && <SurgeLayerPanel gen={gen} onSaved={() => { setSurgeOpen(false); onSaved?.(); }} testId={`surge-layer-${gen.seq}`} />}
          {mixOpen && <MixBusPanel id={gen.id} kind="music" seq={gen.seq} onSaved={() => { setMixOpen(false); onSaved?.(); }} />}

          {aiMetas.length > 0 && (
            <div className={`mt-3 grid gap-3 ${aiMetas.length > 1 ? "sm:grid-cols-2" : ""}`} data-testid={`ai-meta-result-${gen.seq}`}>
              {aiMetas.map((entry) => (
                <div key={entry.provider} className="rounded-xl border border-primary/25 bg-primary/[0.04] p-3 flex flex-col" data-testid={`ai-meta-${entry.provider}-${gen.seq}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono text-primary">AI SUGGESTION</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-secondary/15 text-secondary border border-secondary/30">{PROVIDER_LABEL[entry.provider]}</span>
                  </div>
                  <div className="font-display font-bold">{entry.data.title}</div>
                  {entry.data.description && <p className="text-xs text-muted-foreground mt-0.5 flex-1">{entry.data.description}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(entry.data.tags || []).map((t) => (
                      <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-secondary/15 text-secondary border border-secondary/30">#{t}</span>
                    ))}
                  </div>
                  <Button onClick={() => applyMeta(entry)} disabled={applied === entry.provider} size="sm" className="mt-3 h-8 bg-secondary text-secondary-foreground hover:bg-secondary/90 gap-1.5 self-start" data-testid={aiMetas.length > 1 ? `apply-metadata-${entry.provider}-${gen.seq}` : `apply-metadata-${gen.seq}`}>
                    {applied === entry.provider ? <><CheckCircle2 size={14} /> Saved to track</> : <>Apply to track</>}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

const Chip = ({ children }) => (
  <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-muted text-muted-foreground border border-border">{children}</span>
);

const MetaBadge = ({ label, value, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId} className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30 hover:bg-primary/30 transition-colors">
    <span className="opacity-70">{label}</span> <span className="font-bold">{value}</span>
  </button>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{k}</span>
    <span>{v}</span>
  </div>
);
